import { BEARER_PREFIX } from '@worklogix/shared';
import type { ErrorEnvelope, Health } from '@worklogix/shared';

/**
 * Typed error thrown for any non-2xx API response. Carries the parsed
 * {@link ErrorEnvelope} fields when the server returned one.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

/** Async supplier of a bearer access token (typically `useAuth().acquireToken`). */
export type TokenProvider = () => Promise<string>;

export interface ApiClientOptions {
  /** API origin, from `VITE_API_BASE`. Trailing slash is tolerated. */
  baseUrl: string;
  /** Supplies a bearer token for authenticated requests. */
  getToken: TokenProvider;
  /**
   * Invoked on a 401 so the caller can trigger re-authentication (e.g. MSAL
   * redirect). We do NOT silently retry — that would risk an auth loop.
   */
  onUnauthorized?: () => void;
}

interface RequestOptions {
  method?: string;
  /** JSON-serializable body; `Content-Type: application/json` is set when present. */
  body?: unknown;
  /** When false, no Authorization header is attached (e.g. public /health). */
  auth?: boolean;
  signal?: AbortSignal;
}

export interface ApiClient {
  /** Generic typed request. Resolves the parsed JSON body as `T`. */
  request: <T>(path: string, options?: RequestOptions) => Promise<T>;
  /** Liveness probe — `GET /health`, unauthenticated. */
  getHealth: () => Promise<Health>;
}

function joinUrl(baseUrl: string, path: string): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

async function parseErrorEnvelope(response: Response): Promise<ErrorEnvelope['error'] | null> {
  try {
    const data = (await response.json()) as Partial<ErrorEnvelope>;
    if (data.error && typeof data.error.code === 'string') {
      return data.error;
    }
  } catch {
    // Non-JSON or empty body — fall through to a generic error.
  }
  return null;
}

/**
 * Create an API client bound to a token provider. Attaches `Authorization:
 * Bearer <token>` on authenticated calls, throws {@link ApiError} on non-2xx,
 * and routes 401 to `onUnauthorized` (single re-auth trigger, never a retry loop).
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, auth = true, signal } = opts;

    const headers = new Headers();
    if (body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }
    if (auth) {
      const token = await options.getToken();
      headers.set('Authorization', `${BEARER_PREFIX}${token}`);
    }

    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }
    if (signal !== undefined) {
      init.signal = signal;
    }

    const response = await fetch(joinUrl(options.baseUrl, path), init);

    if (response.status === 401) {
      options.onUnauthorized?.();
      throw new ApiError(401, 'UNAUTHORIZED', 'Authentication required.');
    }

    if (!response.ok) {
      const envelope = await parseErrorEnvelope(response);
      throw new ApiError(
        response.status,
        envelope?.code ?? 'HTTP_ERROR',
        envelope?.message ?? `Request failed with status ${response.status}.`,
        envelope?.requestId
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  return {
    request,
    getHealth: () => request<Health>('/health', { auth: false }),
  };
}
